<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import BehaviorTab from './components/BehaviorTab.vue';
import ConfigTab from './components/ConfigTab.vue';
import KnowledgeTab from './components/KnowledgeTab.vue';
import InboxesTab from './components/InboxesTab.vue';
import FlowTab from './components/FlowTab.vue';
import CatalogTab from './components/CatalogTab.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const agentId = computed(() => Number(route.params.id));
const agent = computed(() => store.getters['aiAgents/getAgent'](agentId.value));
const activeTab = ref('behavior');

const tabs = [
  { key: 'behavior', label: t('AI_AGENTS.TABS.BEHAVIOR') },
  { key: 'config', label: t('AI_AGENTS.TABS.CONFIG') },
  { key: 'flow', label: t('AI_AGENTS.TABS.FLOW') },
  { key: 'knowledge', label: t('AI_AGENTS.TABS.KNOWLEDGE') },
  { key: 'inboxes', label: t('AI_AGENTS.TABS.INBOXES') },
  { key: 'catalog', label: t('AI_AGENTS.TABS.CATALOG') },
];

const activeTabDescription = computed(() =>
  t(`AI_AGENTS.TABS.${activeTab.value.toUpperCase()}_DESCRIPTION`)
);

onMounted(() => {
  store.dispatch('aiAgents/show', agentId.value);
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 overflow-y-auto px-6 py-4">
    <div class="flex items-center gap-4 mb-6">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ agent.name }}
      </h1>
      <span
        class="px-2 py-1 text-xs rounded-full"
        :class="
          agent.status === 0
            ? 'bg-n-emerald-9/10 text-n-emerald-11'
            : 'bg-n-slate-7 text-n-slate-11'
        "
      >
        {{
          agent.status === 0
            ? t('AI_AGENTS.STATUSES.ACTIVE')
            : t('AI_AGENTS.STATUSES.INACTIVE')
        }}
      </span>
    </div>

    <div class="flex gap-2 mb-6 border-b border-n-slate-6">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
        :class="
          activeTab === tab.key
            ? 'border-n-brand text-n-brand'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <p class="text-sm text-n-slate-10 mb-6">
      {{ activeTabDescription }}
    </p>

    <BehaviorTab
      v-if="activeTab === 'behavior'"
      :agent="agent"
      @saved="store.dispatch('aiAgents/show', agentId)"
    />
    <ConfigTab
      v-else-if="activeTab === 'config'"
      :agent="agent"
      @saved="store.dispatch('aiAgents/show', agentId)"
    />
    <FlowTab
      v-else-if="activeTab === 'flow'"
      :agent="agent"
      @saved="store.dispatch('aiAgents/show', agentId)"
    />
    <KnowledgeTab v-else-if="activeTab === 'knowledge'" :agent="agent" />
    <InboxesTab v-else-if="activeTab === 'inboxes'" :agent="agent" />
    <CatalogTab v-else-if="activeTab === 'catalog'" :agent="agent" />
  </div>
</template>
